import { IdeasFileNotFound } from "@inscopix/ideas-hyperformula";
import { useDataTableContext } from "../store/DataTableProvider";
import { isDefined } from "utils/isDefined";
import { sumBy } from "lodash";
import { filesize } from "utils/filesize";
import { EuiAccordion, EuiPanel } from "@inscopix/ideas-eui";

type UseFilesToDeleteForCellRangesProps =
  | { tableId: string }
  | { columnId: string }
  | { rowIds: string[] };

/**
 * Hook that determines the files that will be deleted as a result of deleting
 * cells within the specified address ranges.
 *
 * For data tables, any file assigned to a cell is deleted if it is only
 * referenced by the deleted table. If the file is referenced in multiple
 * tables, the file is not deleted. The same rules apply for deleting data table
 * columns and rows.
 *
 * For analysis tables, only result files are deleted. Input files, whether they
 * were assigned directly to the analysis table or not, are not deleted. The
 * same rules apply for deleting analysis table columns and rows.
 *
 * @param target - The target table, column or rows of the delete operation.
 * @returns The files that will be deleted.
 */
export const useFilesToDeleteForCellRanges = (
  target: UseFilesToDeleteForCellRangesProps,
) => {
  return useDataTableContext((s) => {
    let assignments: ReturnType<typeof s.getFileAssignments> = {};

    // Delete result files if analysis table. Delete files referenced once if
    // data table.
    if ("tableId" in target) {
      const table = s.tables.find((t) => t.id === target.tableId);
      if (table?.kind === "analysis") {
        const resultColumns = table.columns.filter((c) => c.is_result);
        assignments = s.getFileAssignments({
          addressRanges: resultColumns.map((c) => ({ columnId: c.id })),
        });
      } else if (table?.kind === "data") {
        assignments = s.getFileAssignments({
          addressRanges: [{ tableId: table.id }],
          maxRefCount: 1,
        });
      }
    }
    // Only data table columns can be deleted
    else if ("columnId" in target) {
      assignments = s.getFileAssignments({
        addressRanges: [{ columnId: target.columnId }],
        maxRefCount: 1,
      });
    }
    // Delete result files if analysis table. Delete files referenced once if
    // data table.
    else if ("rowIds" in target) {
      const table = s.tables.find((t) =>
        t.rows.some((r) => target.rowIds.includes(r.id)),
      );
      if (table?.kind === "analysis") {
        const resultColumns = table.columns.filter((c) => c.is_result);
        const addressRanges = resultColumns.flatMap((c) => {
          return target.rowIds.map((rowId) => ({
            columnId: c.id,
            rowId,
          }));
        });
        assignments = s.getFileAssignments({ addressRanges });
      } else if (table?.kind === "data") {
        assignments = s.getFileAssignments({
          addressRanges: target.rowIds.map((id) => ({ rowId: id })),
          maxRefCount: 1,
        });
      }
    }

    return Object.keys(assignments)
      .map((fileId) => {
        const file = s.fileMap.filesById[fileId];
        return file !== IdeasFileNotFound ? file : undefined;
      })
      .filter(isDefined);
  });
};

interface FileAssignmentsWarningProps {
  files: { id: string; size: number; name: string }[];
}

/**
 * Component that renders a warning message indicating the number of files that
 * will be deleted and the total amount of storage space that will be freed as a
 * result of performing a deletion operation.
 */
export const FileAssignmentsWarning = ({
  files,
}: FileAssignmentsWarningProps) => {
  const totalSize = sumBy(files, (f) => f.size);
  return (
    <EuiAccordion
      id="file-assignments-warning-accordion"
      buttonContent={
        <span>
          <b>{files.length} file(s)</b> will be deleted, freeing{" "}
          <b>{filesize(totalSize)}</b> of storage space (click to expand file
          list).
        </span>
      }
    >
      <EuiPanel color="subdued">
        {files.map((file) => (
          <p key={file.id} style={{ marginBottom: 4 }}>
            {file.name} ({filesize(file.size)})
          </p>
        ))}
      </EuiPanel>
    </EuiAccordion>
  );
};
